// frontend/client/src/components/Profile.jsx
// FIXED: /users/social-connections → /social/connections
// FIXED: Dark mode visibility for input texts, placeholders, textareas, and active icons.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/apiClient';
import { User, Link2, MessageSquare, Palette, Loader2, Save, RefreshCw } from 'lucide-react';

const TABS = [
  { id: 'basic',  label: 'Basic Info',            icon: User },
  { id: 'social', label: 'Social Channels',        icon: Link2 },
  { id: 'voice',  label: 'Brand Voice',            icon: MessageSquare },
  { id: 'visual', label: 'Visual Identity',        icon: Palette },
];

// ✅ Must match User.js schema enums exactly
const TONE_OPTIONS = [
  { id: 'professional', label: 'Professional' },
  { id: 'casual',       label: 'Casual' },
  { id: 'witty',        label: 'Witty' },
  { id: 'inspiring',    label: 'Inspiring' },
  { id: 'educational',  label: 'Educational' },
  { id: 'edgy',         label: 'Edgy' },
  { id: 'warm',         label: 'Warm' },
  { id: 'luxury',       label: 'Luxury' },
];

const VISUAL_STYLE_OPTIONS = [
  { id: 'minimal',  label: 'Minimal',   desc: 'Clean, lots of white space' },
  { id: 'bold',     label: 'Bold',      desc: 'High contrast, strong type' },
  { id: 'earth',    label: 'Earth',     desc: 'Warm, natural tones' },
  { id: 'pastel',   label: 'Pastel',    desc: 'Soft, light palette' },
  { id: 'dark',     label: 'Dark',      desc: 'Sleek dark backgrounds' },
  { id: 'gradient', label: 'Gradient',  desc: 'Vibrant color transitions' },
];

const PLATFORM_CONFIG = {
  instagram: { label: 'Instagram', color: '#E1306C', status: 'live', statusLabel: 'Live' },
  tiktok:    { label: 'TikTok',    color: '#000000', status: 'sandbox', statusLabel: 'Sandbox' },
  linkedin:  { label: 'LinkedIn',  color: '#0A66C2', status: 'coming_soon', statusLabel: 'Coming Soon' },
  twitter:   { label: 'X / Twitter', color: '#000000', status: 'coming_soon', statusLabel: 'Coming Soon' },
};

const STATUS_STYLES = {
  live:        { bg: '#dcfce7', color: '#15803d', dot: '#16a34a', label: 'Live' },
  sandbox:     { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: 'Sandbox' },
  coming_soon: { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: 'Coming Soon' },
};

// Injection of placeholder styling overrides for dark mode rendering compatibility
const PROFILE_STYLES = `
  .profile-input::placeholder {
    color: var(--text-light) !important;
    opacity: 0.7;
  }
  .profile-input:focus {
    border-color: var(--brand-primary) !important;
    box-shadow: 0 0 0 2px var(--brand-secondary);
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .profile-root { padding: 16px !important; }
    .profile-grid { grid-template-columns: 1fr !important; }
    .profile-sidebar { flex-direction: row !important; overflow-x: auto; gap: 8px !important; padding-bottom: 8px; }
    .profile-sidebar button { white-space: nowrap; flex-shrink: 0; }
    .profile-panel { padding: 20px !important; }
    .profile-two-col { grid-template-columns: 1fr !important; }
    .profile-tone-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .profile-style-grid { grid-template-columns: 1fr !important; }
    .profile-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
  }
`;

export default function Profile() {
  const { userData, refreshUserData } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    username: '',
    displayName: '',
    brandName: '',
    industry: '',
    targetAudience: '',
    bio: '',
    website: '',
    brandTone: [],
    contentPillars: [],
    visualStyle: '',
  });

  const [socialStatuses, setSocialStatuses] = useState({
    instagram: { connected: false, username: null },
    tiktok:    { connected: false, username: null },
    linkedin:  { connected: false, username: null },
    twitter:   { connected: false, username: null },
  });

  // Populate form from userData
  useEffect(() => {
    if (userData) {
      setFormData({
        username:       userData.username       || '',
        displayName:    userData.displayName    || '',
        brandName:      userData.brandName      || '',
        industry:       userData.industry       || '',
        targetAudience: userData.targetAudience || '',
        bio:            userData.bio            || '',
        website:        userData.website        || '',
        brandTone:      userData.brandTone      || [],
        contentPillars: userData.contentPillars || [],
        visualStyle:    userData.visualStyle    || '',
      });
    }
  }, [userData]);

  // FIXED: was calling a separate /social/connections endpoint that either
  // doesn't exist or returns the wrong shape, silently failing and showing
  // every platform as "Not connected" — even when userData.connectedAPIs
  // (the same data the dashboard reads, known reliable) says otherwise.
  // Now derives directly from userData so there's only one source of truth.
  useEffect(() => {
    const apis = userData?.connectedAPIs;
    if (!apis) return;
    setSocialStatuses({
      instagram: { connected: !!apis.instagram?.connected, username: apis.instagram?.username || null },
      tiktok:    { connected: !!apis.tiktok?.connected,    username: apis.tiktok?.username || null },
      linkedin:  { connected: !!apis.linkedin?.connected,  username: apis.linkedin?.username || null },
      twitter:   { connected: !!apis.twitter?.connected,   username: apis.twitter?.username || null },
    });
  }, [userData]);

  // "Refresh" now just re-syncs the MongoDB profile (the proven-working path)
  // rather than hitting a separate, unverified endpoint.
  const fetchSocialConnections = useCallback(async () => {
    setSyncing(true);
    try {
      await refreshUserData();
    } catch (err) {
      console.error('[Profile] Failed to refresh profile:', err);
    } finally {
      setSyncing(false);
    }
  }, [refreshUserData]);

  const handleUpdateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTone = (toneId) => {
    setFormData(prev => {
      const current = Array.isArray(prev.brandTone) ? prev.brandTone : [];
      if (current.includes(toneId)) {
        return { ...prev, brandTone: current.filter(t => t !== toneId) };
      } else if (current.length < 3) {
        return { ...prev, brandTone: [...current, toneId] };
      }
      return prev;
    });
  };

  const handleConnectPlatform = async (platform) => {
    try {
      const response = await api.get(`/social/${platform}/auth`);
      if (response && response.url) {
        window.location.href = response.url;
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Failed to initiate ${platform} connection.` });
    }
  };

  const handleDisconnectPlatform = async (platform) => {
    if (!window.confirm(`Disconnect your ${platform} account?`)) return;
    try {
      const response = await api.post(`/social/${platform}/disconnect`);
      if (response && response.success) {
        await refreshUserData();
        setStatusMessage({ type: 'success', text: `${platform} disconnected successfully.` });
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Failed to disconnect ${platform}.` });
    }
  };

  const handleFormSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatusMessage({ type: '', text: '' });
    try {
      const response = await api.put('/users/profile', formData);
      if (response && response.success) {
        setStatusMessage({ type: 'success', text: 'Profile saved successfully!' });
        await refreshUserData();
      } else {
        throw new Error(response?.error || 'Failed to save.');
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: err.message || 'Server error saving profile.' });
    } finally {
      setSaving(false);
    }
  };

  // ✅ FIXED: Applied var(--text-main) and dynamic backgrounds for dark mode legibility
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1px solid var(--border-medium)', fontSize: 14,
    fontFamily: 'var(--font-body)', boxSizing: 'border-box', outline: 'none',
    background: 'var(--bg-canvas)', color: 'var(--text-main)',
    transition: 'all 0.15s ease'
  };

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 700,
    color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 6,
  };

  return (
    <div className="profile-root" style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 20px', fontFamily: 'var(--font-body)' }}>
      <style>{PROFILE_STYLES}</style>

      {/* Page Header */}
      <div className="profile-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, margin: '0 0 3px', color: 'var(--text-main)' }}>
            Brand Profile
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Manage your brand identity and connected social channels
          </p>
        </div>
        <button
          onClick={handleFormSave}
          disabled={saving}
          style={{
            background: 'var(--brand-primary)', color: 'var(--brand-primary-contrast, white)', border: 'none',
            padding: '9px 18px', borderRadius: 10, display: 'flex', alignItems: 'center',
            gap: 7, fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1, boxShadow: 'var(--shadow-brand)', fontFamily: 'var(--font-body)'
          }}
        >
          {saving ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
          Save Profile
        </button>
      </div>

      {/* Status Banner */}
      {statusMessage.text && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          fontSize: 13, fontWeight: 600,
          background: statusMessage.type === 'success' ? 'var(--brand-secondary)' : '#FEF2F2',
          color: statusMessage.type === 'success' ? 'var(--brand-secondary-contrast, var(--brand-primary))' : '#DC2626',
          border: `1px solid ${statusMessage.type === 'success' ? 'var(--brand-border)' : '#FCA5A5'}`,
        }}>
          {statusMessage.text}
        </div>
      )}

      <div className="profile-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28, alignItems: 'start' }}>

        {/* Sidebar Tabs */}
        <nav className="profile-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', borderRadius: 10, border: 'none',
                  background: active ? 'var(--brand-secondary)' : 'transparent',
                  color: active ? 'var(--brand-secondary-contrast, var(--brand-primary))' : 'var(--text-muted)',
                  fontWeight: active ? 700 : 500, fontSize: 13,
                  textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <Icon size={16} style={{ color: active ? 'var(--brand-secondary-contrast, var(--brand-primary))' : 'var(--text-light)' }} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Panel Content */}
        <div className="profile-panel" style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: '26px 28px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-md)' }}>

          {/* ── BASIC INFO ── */}
          {activeTab === 'basic' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-main)' }}>Basic Information</h3>
              <div className="profile-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Username</label>
                  <input type="text" className="profile-input" value={formData.username} onChange={e => handleUpdateField('username', e.target.value)} style={inputStyle} placeholder="@handle" />
                </div>
                <div>
                  <label style={labelStyle}>Display Name</label>
                  <input type="text" className="profile-input" value={formData.displayName} onChange={e => handleUpdateField('displayName', e.target.value)} style={inputStyle} placeholder="Your name" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Brand Name</label>
                <input type="text" className="profile-input" value={formData.brandName} onChange={e => handleUpdateField('brandName', e.target.value)} style={inputStyle} placeholder="Your brand or company name" />
              </div>
              <div>
                <label style={labelStyle}>Industry</label>
                <input type="text" className="profile-input" value={formData.industry} onChange={e => handleUpdateField('industry', e.target.value)} style={inputStyle} placeholder="e.g. Fashion & Beauty" />
              </div>
              <div>
                <label style={labelStyle}>Bio</label>
                <textarea rows={3} className="profile-input" value={formData.bio} onChange={e => handleUpdateField('bio', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Brief description of your brand" />
              </div>
              <div>
                <label style={labelStyle}>Website</label>
                <input type="url" className="profile-input" value={formData.website} onChange={e => handleUpdateField('website', e.target.value)} style={inputStyle} placeholder="https://yourwebsite.com" />
              </div>
            </div>
          )}

          {/* ── SOCIAL CHANNELS ── */}
          {activeTab === 'social' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-main)' }}>Connected Channels</h3>
                <button
                  onClick={fetchSocialConnections}
                  disabled={syncing}
                  style={{ border: 'none', background: 'transparent', color: 'var(--brand-primary)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                >
                  <RefreshCw size={13} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
                  Refresh
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Object.entries(PLATFORM_CONFIG).map(([platform, config]) => {
                  const account = socialStatuses[platform];
                  const status = config.status;
                  const statusStyle = STATUS_STYLES[status];
                  const canConnect = status === 'live' || status === 'sandbox';
                  return (
                    <div
                      key={platform}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 16px', borderRadius: 12, border: '1px solid var(--border-light)',
                        background: account.connected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                        flexWrap: 'wrap', gap: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: account.connected ? statusStyle.dot : 'var(--border-medium)',
                          boxShadow: account.connected ? `0 0 0 3px ${statusStyle.bg}` : 'none',
                        }} />
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: account.connected ? 'var(--brand-secondary-contrast, var(--text-main))' : 'var(--text-main)', display: 'block' }}>
                            {config.label}
                          </span>
                          <span style={{ fontSize: 11, color: statusStyle.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {account.connected ? 'Connected' : statusStyle.label}
                          </span>
                          {account.connected && (
                            <span style={{ fontSize: 12, color: 'var(--brand-secondary-contrast, var(--text-muted))', display: 'block', marginTop: 2 }}>
                              @{account.username || 'connected'}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (!canConnect) return;
                          account.connected ? handleDisconnectPlatform(platform) : handleConnectPlatform(platform);
                        }}
                        disabled={!canConnect}
                        style={{
                          padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: canConnect ? 'pointer' : 'not-allowed', border: 'none',
                          background: account.connected ? '#FEF2F2' : canConnect ? 'var(--brand-primary)' : '#f3f4f6',
                          color: account.connected ? '#EF4444' : canConnect ? 'var(--brand-primary-contrast, white)' : '#9ca3af',
                          opacity: canConnect ? 1 : 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {account.connected ? 'Disconnect' : canConnect ? 'Connect' : 'Coming Soon'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: 'var(--bg-canvas)', border: '1px solid var(--border-light)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text-main)' }}>Platform Status:</strong><br/>
                <span style={{ color: '#15803d' }}>●</span> <span style={{ color: 'var(--text-main)' }}>Live</span> — Full publishing available<br/>
                <span style={{ color: '#f59e0b' }}>●</span> <span style={{ color: 'var(--text-main)' }}>Sandbox</span> — Limited features, pending approval<br/>
                <span style={{ color: '#9ca3af' }}>●</span> <span style={{ color: 'var(--text-main)' }}>Coming Soon</span> — Not yet available for connection
              </div>
            </div>
          )}

          {/* ── BRAND VOICE ── */}
          {activeTab === 'voice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-main)' }}>Brand Voice</h3>
              <div>
                <label style={labelStyle}>Brand Tone — Select up to 3</label>
                <div className="profile-tone-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  {TONE_OPTIONS.map(tone => {
                    const selected = formData.brandTone.includes(tone.id);
                    return (
                      <button
                        key={tone.id}
                        onClick={() => toggleTone(tone.id)}
                        style={{
                          padding: '9px 8px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          border: selected ? '2px solid var(--brand-primary)' : '2px solid var(--border-light)',
                          background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                          color: selected ? 'var(--brand-secondary-contrast, var(--brand-primary))' : 'var(--text-main)',
                          transition: 'all 0.15s', fontFamily: 'var(--font-body)'
                        }}
                      >
                        {tone.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Target Audience</label>
                <input type="text" className="profile-input" value={formData.targetAudience} onChange={e => handleUpdateField('targetAudience', e.target.value)} style={inputStyle} placeholder="e.g. Gen Z creators aged 18-25" />
              </div>
              <div>
                <label style={labelStyle}>Content Pillars</label>
                {(formData.contentPillars.length > 0 ? formData.contentPillars : ['']).map((pillar, i) => (
                  <input
                    key={i}
                    type="text"
                    className="profile-input"
                    value={pillar}
                    onChange={e => {
                      const updated = [...(formData.contentPillars.length > 0 ? formData.contentPillars : [''])];
                      updated[i] = e.target.value;
                      handleUpdateField('contentPillars', updated);
                    }}
                    style={{ ...inputStyle, marginBottom: 8 }}
                    placeholder={`Pillar ${i + 1}`}
                  />
                ))}
                <button
                  onClick={() => handleUpdateField('contentPillars', [...formData.contentPillars, ''])}
                  style={{ fontSize: 12, color: 'var(--brand-primary)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  + Add Pillar
                </button>
              </div>
            </div>
          )}

          {/* ── VISUAL IDENTITY ── */}
          {activeTab === 'visual' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-main)' }}>Visual Identity</h3>
              <div>
                <label style={labelStyle}>Visual Style</label>
                <div className="profile-style-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {VISUAL_STYLE_OPTIONS.map(style => {
                    const selected = formData.visualStyle === style.id;
                    return (
                      <button
                        key={style.id}
                        onClick={() => handleUpdateField('visualStyle', style.id)}
                        style={{
                          padding: '12px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                          border: selected ? '2px solid var(--brand-primary)' : '2px solid var(--border-light)',
                          background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                          transition: 'all 0.15s'
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, display: 'block', color: selected ? 'var(--brand-secondary-contrast, var(--brand-primary))' : 'var(--text-main)', marginBottom: 2 }}>
                          {style.label}
                        </span>
                        <span style={{ fontSize: 11, color: selected ? 'var(--brand-secondary-contrast, var(--brand-primary))' : 'var(--text-muted)' }}>
                          {style.desc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}